// Campaign-audits API client (live).
//
// Full contract from AAPU-6643: 17 endpoints under /api/v1/campaign-audits,
// envelope `{ status, message, data }` (success status 200 | 'OK' | 10 — see
// isApiSuccess). `API_HOST` already supplies `/api/v1`, so paths here are relative
// to `/campaign-audits`. `X-Tenant` + Bearer are added by arClient on every authed
// call (buildTenantWorkspaceHeaders); create/add-files use the authed multipart
// method `arClient.doApiCallMultipart`.

import arClient from '../../util/api_client';

export const CAMPAIGN_AUDITS_ENDPOINT = '/campaign-audits';
export const auditPath = (id) => `${CAMPAIGN_AUDITS_ENDPOINT}/${id}`;

// Writes that change state (create, add files, confirm, run, …) must not be
// auto-re-sent by the client's retry logic: if the API handled the request but the
// response was lost, a re-send creates a duplicate (e.g. two audits from one submit).
const NO_RETRY = { noRetry: true };

// ─── Thin promise wrappers over the callback-style client (mirrors brandSafetyApi) ──
const apiGet = (endpoint, options = {}) =>
  new Promise((resolve) => arClient.doApiCallGet(endpoint, resolve, options));

const apiPost = (endpoint, body = {}, options = {}) =>
  new Promise((resolve) => arClient.doApiCallV2(endpoint, body, resolve, options));

// doApiCallV2 hardcodes POST; only doApiCall honours options.method, so PATCH/PUT/DELETE go here.
const apiPatch = (endpoint, body = {}) =>
  new Promise((resolve) =>
    arClient.doApiCall(endpoint, body, resolve, undefined, { method: 'PATCH' })
  );

const apiPut = (endpoint, body = {}) =>
  new Promise((resolve) =>
    arClient.doApiCall(endpoint, body, resolve, undefined, { method: 'PUT' })
  );

const apiDelete = (endpoint) =>
  new Promise((resolve) =>
    arClient.doApiCall(endpoint, {}, resolve, undefined, { method: 'DELETE' })
  );

const apiMultipart = (endpoint, formData, options = {}) =>
  new Promise((resolve) => arClient.doApiCallMultipart(endpoint, formData, resolve, options));

/**
 * Build the multipart payload for create / add-files. Each `file` is a Redux
 * uploaded-document item carrying the real `originFileObj` (a File/Blob).
 * `campaignId` (contract v7) starts the audit from an existing campaign instead of
 * files — the two are mutually exclusive, so files are never sent with it and no
 * `name` is sent (the BE names the audit after the campaign). Notes may accompany it.
 * @param {{ files?: Array, name?: string, text?: string, campaignIds?: Array<number>, campaignId?: number }} params
 * @returns {FormData}
 */
export const buildCampaignAuditFormData = ({
  files = [],
  name,
  text,
  campaignIds = [],
  campaignId = null,
} = {}) => {
  const formData = new FormData();
  if (campaignId != null) {
    formData.append('campaignId', campaignId);
    if (text) formData.append('text', text);
    return formData;
  }
  files.forEach((file) => {
    const blob = file?.originFileObj;
    if (blob instanceof Blob) {
      formData.append('files[]', blob, file?.name);
    } else if (file instanceof Blob) {
      formData.append('files[]', file, file?.name);
    }
  });
  const resolvedName = name || files[0]?.name;
  if (resolvedName) formData.append('name', resolvedName);
  if (text) formData.append('text', text);
  campaignIds.forEach((id) => formData.append('campaignIds[]', id));
  return formData;
};

// ─── Endpoints ────────────────────────────────────────────────────────────────

/** GET /campaign-audits — list rows visible to the workspace. */
export const listCampaignAudits = ({ status = null, sort = 'createdAt', order = 'DESC' } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (sort) params.set('sort', sort);
  if (order) params.set('order', order);
  const qs = params.toString();
  return apiGet(`${CAMPAIGN_AUDITS_ENDPOINT}${qs ? `?${qs}` : ''}`);
};

/** POST /campaign-audits — create (authed multipart). 5-min timeout for this call only. */
export const createCampaignAudit = ({
  files = [],
  name,
  text,
  campaignIds = [],
  campaignId = null,
} = {}) =>
  apiMultipart(
    CAMPAIGN_AUDITS_ENDPOINT,
    buildCampaignAuditFormData({ files, name, text, campaignIds, campaignId }),
    { timeout: 300000, ...NO_RETRY }
  );

/** GET /campaign-audits/{id} — full audit (session + sources). */
export const getCampaignAudit = (id) => apiGet(auditPath(id));

/** PUT /campaign-audits/{id} — update name / campaigns. */
export const updateCampaignAudit = (id, { name, campaignIds } = {}) =>
  apiPut(auditPath(id), { ...(name ? { name } : {}), ...(campaignIds ? { campaignIds } : {}) });

/** PATCH /campaign-audits/{id}/rename. */
export const renameCampaignAudit = (id, name) => apiPatch(`${auditPath(id)}/rename`, { name });

/** DELETE /campaign-audits/{id}. */
export const deleteCampaignAudit = (id) => apiDelete(auditPath(id));

/** POST /campaign-audits/{id}/archives. */
export const archiveCampaignAudit = (id) => apiPost(`${auditPath(id)}/archives`, {}, NO_RETRY);

/** POST /campaign-audits/{id}/unarchive — restore an archived audit (no body). */
export const unarchiveCampaignAudit = (id) => apiPost(`${auditPath(id)}/unarchive`, {}, NO_RETRY);

/** POST /campaign-audits/{id}/files — append sources and re-ingest (authed multipart). */
export const addCampaignAuditFiles = (id, { files = [], text, name } = {}) =>
  apiMultipart(
    `${auditPath(id)}/files`,
    buildCampaignAuditFormData({ files, text, name }),
    NO_RETRY
  );

/** DELETE /campaign-audits/{id}/sources/{sourceId} — drop a refused/unreadable source. */
export const deleteCampaignAuditSource = (id, sourceId) =>
  apiDelete(`${auditPath(id)}/sources/${sourceId}`);

/** POST /campaign-audits/{id}/sources/retry — re-queue the remaining sources; ingest events follow. */
export const retryCampaignAuditSources = (id) =>
  apiPost(`${auditPath(id)}/sources/retry`, {}, NO_RETRY);

/** POST /campaign-audits/{id}/messages — clarification text ("field: answer" lines). */
export const sendCampaignAuditMessage = (id, text) =>
  apiPost(`${auditPath(id)}/messages`, { text }, NO_RETRY);

/** GET /campaign-audits/{id}/summary — live ML summary for the confirm screen (3–5s). */
export const getCampaignAuditSummary = (id) =>
  apiGet(`${auditPath(id)}/summary`, { timeout: 30000 });

/** POST /campaign-audits/{id}/confirmations — accept/correct the summary. */
export const confirmCampaignAudit = (id, corrections = {}) =>
  apiPost(`${auditPath(id)}/confirmations`, corrections, NO_RETRY);

/** POST /campaign-audits/{id}/runs — queue analysis (returns immediately). */
export const runCampaignAudit = (id) => apiPost(`${auditPath(id)}/runs`, {}, NO_RETRY);

/** GET /campaign-audits/{id}/progress — websocket fallback only. */
export const getCampaignAuditProgress = (id) => apiGet(`${auditPath(id)}/progress`);

/** GET /campaign-audits/{id}/report — available after the first run completes (404 until analyzed). */
export const getCampaignAuditReport = (id) => apiGet(`${auditPath(id)}/report`);

/** GET /campaign-audits/{id}/campaigns. */
export const getCampaignAuditCampaigns = (id) => apiGet(`${auditPath(id)}/campaigns`);

/** POST /campaign-audits/{id}/campaigns — full replace. */
export const setCampaignAuditCampaigns = (id, campaignIds = []) =>
  apiPost(`${auditPath(id)}/campaigns`, { campaignIds });

/** DELETE /campaign-audits/{id}/campaigns/{campaignId} — detach one. */
export const detachCampaignAuditCampaign = (id, campaignId) =>
  apiDelete(`${auditPath(id)}/campaigns/${campaignId}`);
